import { api, API_ENDPOINTS } from './api';

// Types matching the backend DTOs
export interface Child {
  id: number;
  name: string;
  age: number | null;
  grade: string;
  avatar: string;
  school: string;
  status: 'active' | 'inactive';
  lastActive: string;
  totalCourses: number;
  completedCourses: number;
  currentStreak: number;
  totalStudyTime: number; // in minutes
  averageScore: number | null;
  parentNotes: string;
  courses: ChildCourse[];
  activities: ChildActivity[];
  achievements: ChildAchievement[];
  insights: ChildLearningInsight[];
}

export interface ChildCourse {
  id: number;
  name: string;
  subject: string;
  progress: number;
  lastAccessed: string;
  status: 'in_progress' | 'completed' | 'not_started';
  currentTopic: string;
  nextDeadline: string | null;
  averageScore: number | null;
  timeSpent: number; // in minutes
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface ChildActivity {
  id: number;
  type: 'lesson_completed' | 'quiz_passed' | 'quiz_failed' | 'assignment_submitted' | 'badge_earned' | 'course_started' | 'study_session' | 'help_requested';
  title: string;
  description: string;
  timestamp: string;
  score?: number | null;
  timeSpent?: number | null;
  courseName: string;
  status: 'success' | 'warning' | 'error' | 'info';
}

export interface ChildAchievement {
  id: number;
  title: string;
  description: string;
  icon: string;
  earnedDate: string;
  category: 'academic' | 'behavioral' | 'participation' | 'creativity';
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export interface ChildLearningInsight {
  id: number;
  type: 'strength' | 'improvement' | 'concern' | 'recommendation';
  title: string;
  description: string;
  actionable: boolean;
  priority: 'low' | 'medium' | 'high';
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  timestamp: string;
  errorCode?: string;
}

class ChildDashboardService {
  /**
   * Get child dashboard data
   */
  async getChildDashboard(childId: number): Promise<Child> {
    try {
      const response = await api.get<ApiResponse<Child>>(
        `/api/children/${childId}/dashboard`
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch child dashboard');
      }
    } catch (error: any) {
      console.error('Error fetching child dashboard:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch child dashboard'
      );
    }
  }
}

export const childDashboardService = new ChildDashboardService();

